// Provides a temporary directory for the NodeJS environment,

import { FileSelection, NamedBlob } from './dicom-common';

export interface TempDirFilter {
    initialize(): Promise<void>;
    cleanup(): Promise<void>;

    // Real implementation replaces the NamedBlob with one that reads from disk.
    process(input: NamedBlob): Promise<NamedBlob | string>;

    // Real implementation expects to receive strings previously returned by process()
    // and returns FileSelection pointing to the temp directory and these files.
    toWorker(files: NamedBlob[] | string[]): NamedBlob[] | FileSelection;
}

export class DummyTempDirFilter implements TempDirFilter {
    public async initialize() {
        // Nothing to do
    }
    public async cleanup() {
        // Nothing to do
    }
    public async process(input: NamedBlob): Promise<NamedBlob> {
        return input;
    }
    public toWorker(files: NamedBlob[]): NamedBlob[] | FileSelection {
        return files;
    }
}

// This helps us move files to a temporary directory on disk, so that
// the web worker can access them via file paths instead of keeping them in memory.
// This is only available in the Node.js environment.
export class NodeTempDirFilter implements TempDirFilter {
    private fs?: typeof import('fs');
    private path?: typeof import('path');

    private tempPath?: string;

    constructor() {
    }

    public async initialize() {
        this.fs = await import('fs');
        this.path = await import('path');

        const os = await import('os');
        const fs = this.fs;
        const path = this.path;

        // Create a random temp dir
        const tmpDir = os.tmpdir(); // system temp directory
        const randomName = Math.random().toString(36).substring(2, 15); // random string
        this.tempPath = path.join(tmpDir, randomName);

        await fs.promises.mkdir(this.tempPath);
    }

    public async cleanup() {
        if (this.fs && this.tempPath) {
            await this.fs.promises.rm(this.tempPath, { recursive: true, force: true });
            this.tempPath = undefined;
        }
    }

    public async process(file: NamedBlob): Promise<string> {
        if (!this.fs || !this.tempPath)
            throw new Error('NodeTempDir not initialized');

        const fs = this.fs;
        const path = this.path;

        const filePath = path.join(this.tempPath, file.name);
        
        // Create nested subdirectories if file.name contains slashes
        const dirPath = path.dirname(filePath);
        await fs.promises.mkdir(dirPath, { recursive: true });
        
        await fs.promises.writeFile(filePath, new DataView(await file.data.arrayBuffer()));

        return file.name;
    }

    public toWorker(files: string[]): FileSelection {
        const fs: FileSelection = {
            root: this.tempPath!,
            files,
        };
        return fs;
    }
}
